import axios from 'axios'
import {
  UPLOAD_ROUTE_FILE,
  RECEIVE_TRACE_ROUTE_RESULTS,
  RECEIVE_ANALYZE_ROUTE_RESULTS,
  CLEAR_ANALYZE_RESULTS,
  RECEIVE_ROUTE_RESULTS,
  TOGGLE_GPS_TRACE,
} from './types'

import {
  VALHALLA_OSM_URL,
  DRIVEPOINTS_BACKEND_URL,
  buildTraceRouteRequest,
  parseDirectionsGeometry,
} from '../utils/valhalla'
import { drivepointsRequestHeaders } from '../utils/drivepointsApi'
import { toApiDateTime } from '../utils/dateTimeLocal'

import {
  sendMessage,
  showLoading,
  filterProfileSettings,
  zoomTo,
} from './commonActions'

export const uploadRouteFile = (parsedData) => ({
  type: UPLOAD_ROUTE_FILE,
  payload: parsedData,
})

export const clearAnalyzeResults = () => ({
  type: CLEAR_ANALYZE_RESULTS,
})

export const traceRoute =
  ({ parsedData, auth, profile, settings, dateTime } = {}) =>
  (dispatch, getState) => {
    if (!auth) {
      console.error('[traceRoute] auth is undefined')
      dispatch(
        sendMessage({
          type: 'error',
          icon: 'error',
          description: 'Authentication required to trace route',
          title: 'Error',
        })
      )
      return
    }

    if (!parsedData || !parsedData.points || parsedData.points.length === 0) {
      dispatch(
        sendMessage({
          type: 'error',
          icon: 'error',
          description: 'No route data available',
          title: 'Error',
        })
      )
      return
    }

    dispatch(showLoading(true))

    let authHeaders = {}
    if (
      auth &&
      (DRIVEPOINTS_BACKEND_URL.includes('drivepoints.com') ||
        DRIVEPOINTS_BACKEND_URL.includes('localhost'))
    ) {
      authHeaders = {
        Authorization: `Bearer ${auth?.user?.access_token}`,
      }
    }

    const traceRequest = buildTraceRouteRequest({
      profile,
      points: parsedData.points,
      settings,
      dateTime,
    })

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...drivepointsRequestHeaders({ requireIdempotency: true }),
      },
    }

    // Use trace_route endpoint
    const traceUrl = DRIVEPOINTS_BACKEND_URL
      ? `${DRIVEPOINTS_BACKEND_URL}/trace_route`
      : `${VALHALLA_OSM_URL}/trace_route`

    // For trace_route, send as JSON body
    const requestBody = DRIVEPOINTS_BACKEND_URL
      ? { params: traceRequest }
      : traceRequest

    axios
      .post(traceUrl, requestBody, config)
      .then(({ data }) => {
        if (data.ok && data.data) {
          // Adjust to the new API response format
          data = data.data
        }
        // Parse geometry for main route
        if (data.trip && data.trip.legs) {
          data.decodedGeometry = parseDirectionsGeometry(data)
        }

        // Parse geometry for alternates if they exist
        if (data.alternates && Array.isArray(data.alternates)) {
          for (let i = 0; i < data.alternates.length; i++) {
            const alternate = data.alternates[i]
            if (alternate.trip && alternate.trip.legs) {
              data.alternates[i].decodedGeometry =
                parseDirectionsGeometry(alternate)
            }
          }
        }

        dispatch({
          type: RECEIVE_TRACE_ROUTE_RESULTS,
          payload: {
            data,
            parsedData,
          },
        })
        // Register trace route for map display
        dispatch({
          type: RECEIVE_ROUTE_RESULTS,
          payload: {
            provider: 'ANALYZE_TRACE',
            data,
          },
        })
        if (data.decodedGeometry) {
          dispatch(zoomTo(data.decodedGeometry))
        }
      })
      .catch(({ response }) => {
        let error_msg = 'Failed to trace route'
        if (response && response.data) {
          error_msg = response.data.error || error_msg
        }
        dispatch(
          sendMessage({
            type: 'error',
            icon: 'error',
            description: error_msg,
            title: 'Trace Route Error',
          })
        )
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(showLoading(false))
        }, 500)
      })
  }

export const analyzeRoute =
  ({ parsedData, auth, profile, settings, dateTime } = {}) =>
  (dispatch, getState) => {
    if (!auth) {
      console.error('[analyzeRoute] auth is undefined')
      return
    }

    dispatch(showLoading(true))

    // const { profile: currentProfile } = getState().common
    const { settings: currentSettings } = getState().common
    const { dateTime: currentDateTime } = getState().common

    const activeSettings = settings || currentSettings
    const activeDateTime = dateTime || currentDateTime

    // Build route requests for different profiles
    const profilesToAnalyze = DRIVEPOINTS_BACKEND_URL
      ? ['autodp', 'truckdp', 'car', 'truck']
      : ['car', 'truck']

    const routePromises = profilesToAnalyze.map((prof) => {
      const filteredSettings = filterProfileSettings(prof, activeSettings)
      return makeAnalyzeRequest({
        profile: prof,
        start: parsedData.start,
        end: parsedData.end,
        settings: filteredSettings,
        dateTime: activeDateTime,
        auth,
      })(dispatch)
    })

    Promise.all(routePromises)
      .then((results) => {
        // Register each analyzed route for map display
        results.forEach((result) => {
          if (result.data) {
            dispatch({
              type: RECEIVE_ROUTE_RESULTS,
              payload: {
                provider: `ANALYZE_${result.profile.toUpperCase()}`,
                data: result.data,
              },
            })
          }
        })
        dispatch({
          type: RECEIVE_ANALYZE_ROUTE_RESULTS,
          payload: results,
        })
      })
      .catch((error) => {
        console.error('[analyzeRoute] error', error)
        dispatch(
          sendMessage({
            type: 'error',
            icon: 'error',
            description: 'Failed to analyze some routes',
            title: 'Analysis Error',
          })
        )
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(showLoading(false))
        }, 500)
      })
  }

export const makeAnalyzeRequest =
  ({ profile, start, end, settings, dateTime, auth } = {}) =>
  () => {
    return new Promise((resolve, reject) => {
      let valhalla_profile = profile
      if (profile === 'car' || profile === 'autodp') {
        valhalla_profile = 'auto'
      } else if (profile === 'truck' || profile === 'truckdp') {
        valhalla_profile = 'truck'
      }

      const request = {
        costing: valhalla_profile,
        costing_options: {
          [valhalla_profile]: { ...settings.costing },
        },
        locations: [
          { lat: start.lat, lon: start.lon, type: 'break' },
          { lat: end.lat, lon: end.lon, type: 'break' },
        ],
        units: 'miles',
        id: `analyze_${profile}`,
      }

      if (dateTime && dateTime.type > -1) {
        const apiDateTime = toApiDateTime(dateTime)
        if (apiDateTime) {
          request.date_time = apiDateTime
          // Match buildDirectionsRequest: enable alternates-capable search for depart_at/current.
          if (apiDateTime.type === 0 || apiDateTime.type === 1) {
            request.prioritize_bidirectional = true
          }
        }
      }

      let authHeaders = {}
      if (
        auth &&
        (DRIVEPOINTS_BACKEND_URL.includes('drivepoints.com') ||
          DRIVEPOINTS_BACKEND_URL.includes('localhost'))
      ) {
        authHeaders = {
          Authorization: `Bearer ${auth?.user?.access_token}`,
        }
      }

      const config = {
        params: { json: JSON.stringify(request) },
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...drivepointsRequestHeaders({ requireIdempotency: true }),
        },
      }

      const url =
        profile === 'autodp' || profile === 'truckdp'
          ? `${DRIVEPOINTS_BACKEND_URL}/route`
          : `${VALHALLA_OSM_URL}/route`

      if (profile === 'autodp' || profile === 'truckdp') {
        // POST for drivepoints backend
        axios
          .post(url, { params: request }, config)
          .then(({ data }) => {
            if (data.ok && data.data) {
              // Adjust to the new API response format
              data = data.data
            }
            data.decodedGeometry = parseDirectionsGeometry(data)
            resolve({
              profile,
              data,
            })
          })
          .catch((error) => {
            reject(
              new Error(
                `Failed to analyze route for profile ${profile}: ${
                  error.message || error
                }`
              )
            )
          })
      } else {
        // GET for valhalla backend
        axios
          .get(url, config)
          .then(({ data }) => {
            if (data.ok && data.data) {
              // Adjust to the new API response format
              data = data.data
            }
            data.decodedGeometry = parseDirectionsGeometry(data)
            resolve({
              profile,
              data,
            })
          })
          .catch((error) => {
            reject(
              new Error(
                `Failed to analyze route for profile ${profile}: ${
                  error.message || error
                }`
              )
            )
          })
      }
    })
  }

export const toggleGPSTrace = (show) => ({
  type: TOGGLE_GPS_TRACE,
  payload: show,
})
